import React, { useState, useEffect, useCallback } from 'react';
import { Package, Clock, AlertTriangle, CheckCircle, XCircle, Search, RefreshCw, Loader2 } from 'lucide-react';
import batchService from '../../services/batchService';
import { fuzzyMatch } from '../../lib/fuzzySearch';

const STATUS_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
  warning:  { label: 'Warning',  color: 'bg-yellow-100 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500' },
  healthy:  { label: 'Healthy',  color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' },
  expired:  { label: 'Expired',  color: 'bg-gray-100 text-gray-500 border-gray-200', dot: 'bg-gray-400' },
};

const BatchManagement = () => {
  const [activeBatches, setActiveBatches] = useState([]);
  const [expiredBatches, setExpiredBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('active'); // 'active' | 'expired'
  const [searchQuery, setSearchQuery] = useState('');

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [active, expired] = await Promise.all([
        batchService.getActiveBatches(),
        batchService.getExpiredBatches(),
      ]);
      setActiveBatches(active);
      setExpiredBatches(expired);
    } catch (err) {
      setError('Failed to load batch data');
      console.error('Batch fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const filteredActive = activeBatches.filter(b =>
    fuzzyMatch(searchQuery, b.productName)
  );

  const filteredExpired = expiredBatches.filter(b =>
    fuzzyMatch(searchQuery, b.productName)
  );

  // Group active batches by product
  const grouped = {};
  filteredActive.forEach(b => {
    if (!grouped[b.productId]) {
      grouped[b.productId] = { productName: b.productName, batches: [] };
    }
    grouped[b.productId].batches.push(b);
  });

  // Sort products by most urgent batch (lowest daysRemaining first)
  const sortedProducts = Object.entries(grouped).sort((a, b) => {
    const minA = Math.min(...a[1].batches.map(batch => batch.daysRemaining));
    const minB = Math.min(...b[1].batches.map(batch => batch.daysRemaining));
    return minA - minB;
  });

  // Summary stats
  const criticalCount = activeBatches.filter(b => b.status === 'critical').length;
  const warningCount = activeBatches.filter(b => b.status === 'warning').length;
  const healthyCount = activeBatches.filter(b => b.status === 'healthy').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Batch Management</h2>
          <p className="text-sm text-gray-500 mt-1">Track product freshness and shelf life</p>
        </div>
        <button
          onClick={fetchBatches}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
            <Package className="w-4 h-4" />
            Total Active
          </div>
          <p className="text-2xl font-bold text-gray-800">{activeBatches.length}</p>
        </div>
        <div className="bg-red-50 rounded-xl border border-red-200 p-4">
          <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
            <XCircle className="w-4 h-4" />
            Critical (≤1 day)
          </div>
          <p className="text-2xl font-bold text-red-700">{criticalCount}</p>
        </div>
        <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4">
          <div className="flex items-center gap-2 text-yellow-600 text-sm mb-1">
            <AlertTriangle className="w-4 h-4" />
            Warning (≤3 days)
          </div>
          <p className="text-2xl font-bold text-yellow-700">{warningCount}</p>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-200 p-4">
          <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
            <CheckCircle className="w-4 h-4" />
            Healthy
          </div>
          <p className="text-2xl font-bold text-green-700">{healthyCount}</p>
        </div>
      </div>

      {/* View Toggle + Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setView('active')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === 'active' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Active Batches ({activeBatches.length})
          </button>
          <button
            onClick={() => setView('expired')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === 'expired' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Expired ({expiredBatches.length})
          </button>
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by product name..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
          />
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading batches...
        </div>
      )}

      {/* Active Batches View */}
      {!loading && view === 'active' && (
        sortedProducts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Package className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No active batches found</p>
            <p className="text-sm mt-1">Batches are created when products are restocked</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedProducts.map(([productId, { productName, batches }]) => (
              <div key={productId} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Product Header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold text-gray-800">{productName}</span>
                    <span className="text-xs text-gray-500">({batches.length} batch{batches.length > 1 ? 'es' : ''})</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    Shelf life: {batches[0]?.shelfLifeDays || '?'} days
                  </span>
                </div>

                {/* Batch Rows */}
                <div className="divide-y divide-gray-100">
                  {batches.map(batch => {
                    const cfg = STATUS_CONFIG[batch.status] || STATUS_CONFIG.healthy;
                    return (
                      <div key={batch.id} className="px-4 py-3 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                        {/* Status dot */}
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.dot}`} />

                        {/* Batch info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">Batch #{batch.id}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${cfg.color}`}>
                              {cfg.label}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Harvested: {new Date(batch.harvestDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </div>

                        {/* Quantity */}
                        <div className="text-right">
                          <p className="text-sm font-semibold text-gray-800">{batch.currentQuantity} units</p>
                          <p className="text-xs text-gray-400">of {batch.initialQuantity}</p>
                        </div>

                        {/* Days remaining */}
                        <div className="text-right w-20">
                          <div className="flex items-center gap-1 justify-end">
                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                            <span className={`text-sm font-bold ${
                              batch.daysRemaining <= 0 ? 'text-red-600' :
                              batch.daysRemaining <= 1 ? 'text-red-600' :
                              batch.daysRemaining <= 3 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {batch.daysRemaining <= 0 ? 'Expired' : `${batch.daysRemaining}d`}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Expired Batches View */}
      {!loading && view === 'expired' && (
        filteredExpired.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Clock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No expired batches</p>
            <p className="text-sm mt-1">Expired batches will appear here after the daily sweep</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Product</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Harvest Date</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Qty Remaining</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Initial Qty</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Expired At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredExpired.map(batch => (
                  <tr key={batch.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{batch.productName}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {new Date(batch.harvestDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{batch.currentQuantity}</td>
                    <td className="px-4 py-3 text-right text-gray-400">{batch.initialQuantity}</td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {new Date(batch.expiredAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default BatchManagement;